import React from 'react'
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline'
import { toast } from 'sonner'

/**
 * Top-of-page action bar for the PQR Preview. Buttons:
 *   - Discard staged changes   (visible only when isDirty)
 *   - Save                     (DB only, no PDF; enabled even with blanks)
 *   - Save & Generate Report   (DB + PDF; gated on the four required fields)
 *
 * The Change Weld button was removed in `pqr-report-history-and-rev`
 * slice 05: a PQR draft's weld scope is now fixed at picker time on
 * `PqrDetails` and surfaced as a read-only pill in the page header via
 * `PqrPreviewHeaderPills`. To re-scope, the user returns to PqrDetails
 * and starts a fresh draft via "Generate PQR Report".
 *
 * Each button is a thin click target — the parent owns the diff-confirm
 * flow. When Save & Generate is blocked by missing required fields, the
 * button stays interactive (aria-disabled) so a click can surface the
 * exact reason as a toast, and an inline warning is rendered underneath
 * so the same information is permanently visible.
 */

interface Props {
  isDirty: boolean
  busy: boolean
  saveAndGenerateDisabledReason?: string
  onDiscard: () => void
  onSave: () => void
  onSaveAndGenerate: () => void
  onExportExcel?: () => void
}

export default function PqrPreviewToolbar({
  isDirty,
  busy,
  saveAndGenerateDisabledReason,
  onDiscard,
  onSave,
  onSaveAndGenerate,
  onExportExcel
}: Props) {
  const blockedByReason = Boolean(saveAndGenerateDisabledReason)

  const handleSaveAndGenerateAttempt = () => {
    if (busy) return
    if (saveAndGenerateDisabledReason) {
      toast.error(saveAndGenerateDisabledReason)
      return
    }
    onSaveAndGenerate()
  }

  return (
    <div className="flex flex-wrap items-start gap-2">
      {isDirty ? (
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-1 text-hero-red-70"
          onClick={onDiscard}
          disabled={busy}
          data-testid="pqr-toolbar-discard"
        >
          <ArrowUturnLeftIcon className="h-4 w-4" />
          Discard staged changes
        </button>
      ) : null}
      <button
        type="button"
        className="btn-secondary inline-flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={onSave}
        disabled={busy}
        data-testid="pqr-toolbar-save"
      >
        <CheckCircleIcon className="h-4 w-4" />
        Save
      </button>
      {onExportExcel ? (
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onExportExcel}
          disabled={busy}
          data-testid="pqr-toolbar-export-excel"
        >
          <DocumentArrowDownIcon className="h-4 w-4" />
          Export Excel
        </button>
      ) : null}
      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          className={`btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold ${
            busy || blockedByReason
              ? 'cursor-not-allowed opacity-50'
              : ''
          }`}
          onClick={handleSaveAndGenerateAttempt}
          disabled={busy}
          aria-disabled={busy || blockedByReason}
          title={saveAndGenerateDisabledReason}
          data-testid="pqr-toolbar-save-and-generate"
        >
          <DocumentArrowDownIcon className="h-4 w-4" />
          Save &amp; Generate Report
        </button>
        {blockedByReason ? (
          <p
            role="status"
            className="max-w-xs text-right text-xs text-hero-red-70"
            data-testid="pqr-toolbar-save-generate-blocked"
          >
            {saveAndGenerateDisabledReason}
          </p>
        ) : null}
      </div>
    </div>
  )
}
